import { useState } from 'react'
import { ChevronDown, ChevronRight, Wrench, CheckCircle, XCircle, User, Bot } from 'lucide-react'

/**
 * Transcript Display - supports two formats:
 *
 * 1. NEW JSON FORMAT (from LiveKit):
 *    A JSON array of events, each with type, relative_time, and type-specific fields.
 *
 * 2. LEGACY TEXT FORMAT (plain text, fallback):
 *    Plain text transcript, displayed as-is in a preformatted block.
 */

// Types for JSON transcript events
interface TurnEvent {
  type: 'turn'
  role: 'Agent' | 'Callee'
  text: string
  relative_time?: string
}

interface ToolCallEvent {
  type: 'tool_call'
  name: string
  args: unknown
  id?: string
  relative_time?: string
}

interface ToolResultEvent {
  type: 'tool_result'
  name: string
  status: 'success' | 'error'
  result: unknown
  id?: string
  relative_time?: string
}

type TranscriptEvent = TurnEvent | ToolCallEvent | ToolResultEvent

// Check if transcript is new JSON format
function isJsonTranscript(transcript: string): boolean {
  const trimmed = transcript.trim()
  if (!trimmed.startsWith('[')) return false
  try {
    const parsed = JSON.parse(trimmed)
    return Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object' && 'type' in parsed[0]
  } catch {
    return false
  }
}

// Parse JSON transcript
function parseJsonTranscript(transcript: string): TranscriptEvent[] {
  try {
    return JSON.parse(transcript) as TranscriptEvent[]
  } catch {
    return []
  }
}

// Parse MM:SS to seconds
function parseTime(timeStr: string | undefined | null): number {
  if (!timeStr || typeof timeStr !== 'string') return 0
  const parts = timeStr.split(':')
  if (parts.length !== 2) return 0
  const [min, sec] = parts.map(Number)
  if (isNaN(min) || isNaN(sec)) return 0
  return min * 60 + sec
}

// Format seconds to MM:SS
function formatTime(seconds: number): string {
  const min = Math.floor(seconds / 60)
  const sec = seconds % 60
  return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
}

// Adjust timestamp for display
function adjustTimestamp(
  timeStr: string | undefined | null,
  isFirst: boolean
): { display: string; seekSeconds: number } | null {
  if (!timeStr || typeof timeStr !== 'string' || !timeStr.includes(':')) return null
  const seconds = parseTime(timeStr)
  const adjustedSeconds = isFirst ? seconds : Math.max(0, seconds - 1)
  return {
    display: formatTime(adjustedSeconds),
    seekSeconds: adjustedSeconds,
  }
}

// Format any value for display
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return JSON.stringify(parsed, null, 2)
    } catch {
      return value
    }
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2)
  }
  return String(value)
}

// Check if value has meaningful content
function hasContent(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return false
    try {
      const parsed = JSON.parse(trimmed)
      if (parsed === null || parsed === undefined) return false
      if (typeof parsed === 'object' && Object.keys(parsed).length === 0) return false
      return true
    } catch {
      return trimmed.length > 0
    }
  }
  if (typeof value === 'object') {
    return Object.keys(value).length > 0
  }
  return true
}

// Turn display component
function TurnDisplay({
  event,
  onSeek,
  isFirst = false,
}: {
  event: TurnEvent
  onSeek?: (seconds: number) => void
  isFirst?: boolean
}) {
  const isAgent = event.role === 'Agent'
  const timestamp = adjustTimestamp(event.relative_time, isFirst)

  const handleTimestampClick = () => {
    if (!onSeek || !timestamp) return
    onSeek(timestamp.seekSeconds)
  }

  return (
    <div className="flex items-start gap-1.5 py-1">
      <div
        className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center mt-0.5 ${
          isAgent ? 'bg-green-100' : 'bg-gray-200'
        }`}
      >
        {isAgent ? (
          <Bot className="h-2.5 w-2.5 text-green-600" />
        ) : (
          <User className="h-2.5 w-2.5 text-gray-500" />
        )}
      </div>
      <div className={`flex-1 min-w-0 rounded-md px-2 py-1 ${isAgent ? 'bg-green-50' : 'bg-gray-50'}`}>
        <div className="flex items-start justify-between gap-2">
          <p
            className={`text-detail whitespace-pre-wrap break-words flex-1 ${
              isAgent ? 'text-green-900' : 'text-gray-700'
            }`}
          >
            {event.text}
          </p>
          {timestamp && (
            <span
              onClick={handleTimestampClick}
              className={`text-detail text-gray-400 flex-shrink-0 pt-0.5 ${
                onSeek ? 'cursor-pointer hover:text-blue-500 hover:underline' : ''
              }`}
            >
              {timestamp.display}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// Tool Call component
function ToolCallDisplay({
  event,
  onSeek,
  isFirst = false,
}: {
  event: ToolCallEvent
  onSeek?: (seconds: number) => void
  isFirst?: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const hasArgs = hasContent(event.args)
  const timestamp = adjustTimestamp(event.relative_time, isFirst)

  const handleTimestampClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onSeek || !timestamp) return
    onSeek(timestamp.seekSeconds)
  }

  return (
    <div className="my-0.5 border border-blue-200 rounded bg-blue-50">
      <div
        onClick={() => hasArgs && setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-2 py-1 ${hasArgs ? 'cursor-pointer hover:bg-blue-100' : ''} rounded`}
      >
        {hasArgs &&
          (isOpen ? (
            <ChevronDown className="h-2.5 w-2.5 text-blue-600 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-2.5 w-2.5 text-blue-600 flex-shrink-0" />
          ))}
        <Wrench className="h-2.5 w-2.5 text-blue-600 flex-shrink-0" />
        <span className="text-detail font-medium text-blue-700 flex-1 font-mono">{event.name}</span>
        {timestamp && (
          <span
            onClick={handleTimestampClick}
            className={`text-detail text-blue-400 ${
              onSeek ? 'cursor-pointer hover:text-blue-600 hover:underline' : ''
            }`}
          >
            {timestamp.display}
          </span>
        )}
      </div>
      {isOpen && (
        <div className="px-2 py-1.5 border-t border-blue-200 bg-white rounded-b overflow-hidden">
          <pre className="text-detail bg-gray-50 p-1.5 rounded font-mono max-h-40 overflow-y-auto overflow-x-hidden break-all whitespace-pre-wrap">
            {formatValue(event.args)}
          </pre>
        </div>
      )}
    </div>
  )
}

// Tool Result component
function ToolResultDisplay({
  event,
  onSeek,
  isFirst = false,
}: {
  event: ToolResultEvent
  onSeek?: (seconds: number) => void
  isFirst?: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const isError = event.status === 'error'
  const hasData = hasContent(event.result)
  const timestamp = adjustTimestamp(event.relative_time, isFirst)

  const bgColor = isError ? 'bg-red-50' : 'bg-emerald-50'
  const borderColor = isError ? 'border-red-200' : 'border-emerald-200'
  const textColor = isError ? 'text-red-700' : 'text-emerald-700'
  const iconColor = isError ? 'text-red-600' : 'text-emerald-600'
  const timeColor = isError ? 'text-red-400' : 'text-emerald-400'

  const handleTimestampClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onSeek || !timestamp) return
    onSeek(timestamp.seekSeconds)
  }

  return (
    <div className={`my-0.5 border ${borderColor} rounded ${bgColor}`}>
      <div
        onClick={() => hasData && setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-2 py-1 ${
          hasData ? 'cursor-pointer ' + (isError ? 'hover:bg-red-100' : 'hover:bg-emerald-100') : ''
        } rounded`}
      >
        {hasData &&
          (isOpen ? (
            <ChevronDown className={`h-2.5 w-2.5 ${iconColor} flex-shrink-0`} />
          ) : (
            <ChevronRight className={`h-2.5 w-2.5 ${iconColor} flex-shrink-0`} />
          ))}
        {isError ? (
          <XCircle className={`h-2.5 w-2.5 ${iconColor} flex-shrink-0`} />
        ) : (
          <CheckCircle className={`h-2.5 w-2.5 ${iconColor} flex-shrink-0`} />
        )}
        <span className={`text-detail font-medium ${textColor} flex-1 font-mono`}>
          {event.name}
          <span
            className={`ml-1.5 px-1 py-0.5 rounded text-detail ${
              isError ? 'bg-red-200' : 'bg-emerald-200'
            }`}
          >
            {event.status}
          </span>
          {isError && typeof event.result === 'object' && event.result !== null && 'error' in event.result && (
            <span className="ml-1.5 text-detail text-red-600 font-normal">
              {String((event.result as Record<string, unknown>).error).substring(0, 200)}
            </span>
          )}
        </span>
        {timestamp && (
          <span
            onClick={handleTimestampClick}
            className={`text-detail ${timeColor} ${onSeek ? 'cursor-pointer hover:underline' : ''}`}
          >
            {timestamp.display}
          </span>
        )}
      </div>
      {isOpen && (
        <div className={`px-2 py-1.5 border-t ${borderColor} bg-white rounded-b overflow-hidden`}>
          <pre className="text-detail bg-gray-50 p-1.5 rounded font-mono max-h-40 overflow-y-auto overflow-x-hidden break-all whitespace-pre-wrap">
            {formatValue(event.result)}
          </pre>
        </div>
      )}
    </div>
  )
}

// JSON Transcript Display
function JsonTranscriptDisplay({
  events,
  onSeek,
}: {
  events: TranscriptEvent[]
  onSeek?: (seconds: number) => void
}) {
  return (
    <div className="space-y-0.5 min-w-0 overflow-hidden">
      {events.map((event, index) => {
        const isFirst = index === 0
        if (event.type === 'turn') {
          return <TurnDisplay key={index} event={event} onSeek={onSeek} isFirst={isFirst} />
        }
        if (event.type === 'tool_call') {
          return <ToolCallDisplay key={index} event={event} onSeek={onSeek} isFirst={isFirst} />
        }
        if (event.type === 'tool_result') {
          return <ToolResultDisplay key={index} event={event} onSeek={onSeek} isFirst={isFirst} />
        }
        return null
      })}
    </div>
  )
}

// Legacy Text Transcript Display
function LegacyTranscriptDisplay({ transcript }: { transcript: string }) {
  return (
    <div className="text-detail text-gray-700 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-md overflow-x-hidden">
      {transcript}
    </div>
  )
}

interface TranscriptDisplayProps {
  transcript: string | unknown
  onSeek?: (seconds: number) => void
}

// Main Transcript Display
export function TranscriptDisplay({ transcript, onSeek }: TranscriptDisplayProps) {
  const transcriptStr = typeof transcript === 'string' ? transcript : JSON.stringify(transcript)

  if (!transcriptStr || !transcriptStr.trim()) {
    return <div className="text-detail text-gray-500">No transcript available</div>
  }

  // Try JSON format first
  if (isJsonTranscript(transcriptStr)) {
    const events = parseJsonTranscript(transcriptStr)
    if (events.length > 0) {
      return <JsonTranscriptDisplay events={events} onSeek={onSeek} />
    }
  }

  // Fall back to legacy text display
  return <LegacyTranscriptDisplay transcript={transcriptStr} />
}
